import { useEffect, useMemo, useState } from "react";
import API from "../api/axios";
import TablePagination from "../components/TablePagination";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatCurrency(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
}

function getGreeting(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const [now, setNow] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [pandits, setPandits] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // const [selectedRecentOrderIds, setSelectedRecentOrderIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError("");
        const [ordersRes, panditsRes, itemsRes] = await Promise.all([
          API.get("/admin/orders", { params: { limit: 120 } }),
          API.get("/admin/pandits", { params: { status: "all" } }),
          API.get("/items", { params: { limit: 120 } }),
        ]);

        setOrders(ordersRes.data?.data?.orders || []);
        setPandits(panditsRes.data?.data || []);
        setItems(itemsRes.data?.data?.products || []);
      } catch (err) {
        setError(err.response?.data?.message || "Unable to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const metrics = useMemo(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
    const activePandits = pandits.filter((pandit) => pandit.status === "active").length;
    const pendingOrders = orders.filter(
      (order) => String(order.orderStatus || "").toLowerCase() !== "delivered"
    ).length;

    return [
      {
        title: "Today Orders",
        value: totalOrders,
        tone: "text-[#2f1618] dark:text-[#fff3dc]",
        sub: `${pendingOrders} pending`,
      },
      {
        title: "Revenue",
        value: formatCurrency(totalRevenue),
        tone: "text-[#b9144b]",
        sub: "Overall order revenue",
      },
      {
        title: "Active Poojas",
        value: items.length,
        tone: "text-[#1d7a72]",
        sub: "Products in catalog",
      },
      {
        title: "Pandits Active",
        value: activePandits,
        tone: "text-[#2b5da8]",
        sub: `${pandits.length} total pandits`,
      },
    ];
  }, [orders, pandits, items]);

  const weeklyRevenueData = useMemo(() => {
    const map = new Map(WEEK_DAYS.map((day) => [day, { day, revenue: 0, orders: 0 }]));
    orders.forEach((order) => {
      const date = new Date(order.createdAt || order.updatedAt || Date.now());
      const day = WEEK_DAYS[date.getDay()];
      const bucket = map.get(day);
      if (bucket) {
        bucket.revenue += Number(order.totalAmount || 0);
        bucket.orders += 1;
      }
    });
    return WEEK_DAYS.map((day) => map.get(day));
  }, [orders]);

  const cityStats = useMemo(() => {
    const cityMap = new Map();
    orders.forEach((order) => {
      const city = order?.address?.city || "Unknown";
      cityMap.set(city, (cityMap.get(city) || 0) + 1);
    });
    return [...cityMap.entries()]
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [orders]);

  const recentOrders = useMemo(() => orders, [orders]);
  const pagedRecentOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return recentOrders.slice(start, start + pageSize);
  }, [recentOrders, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [recentOrders.length]);
  // const toggleRecentOrderSelection = (orderId, checked) => {
  //   setSelectedRecentOrderIds((current) => {
  //     if (checked) {
  //       return current.includes(orderId) ? current : [...current, orderId];
  //     }
  //     return current.filter((id) => id !== orderId);
  //   });
  // };

  // const toggleAllRecentOrders = (checked) => {
  //   if (checked) {
  //     setSelectedRecentOrderIds(recentOrders.map((order) => order._id));
  //     return;
  //   }
  //   setSelectedRecentOrderIds([]);
  // };

  const greeting = getGreeting(now.getHours());
  const formattedDate = now.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formattedTime = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="space-y-4">
      <section className="relative overflow-hidden rounded-[30px] border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 text-[var(--admin-text)] shadow-[var(--admin-shadow)] md:p-7">
        <div className="absolute -left-12 top-10 h-36 w-36 rounded-full bg-[#102C57]/8 blur-3xl" />
        <div className="absolute right-0 top-0 h-44 w-44 rounded-full bg-[#18b887]/8 blur-3xl" />

        <div className="relative grid gap-5 xl:grid-cols-[1.45fr_0.95fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[var(--admin-primary)]">
              Dashboard Overview
            </p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight md:text-4xl">
              {greeting}, welcome Admin.
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-[var(--admin-muted)] md:text-base">
              Track orders, revenue, pandits, and product performance from one place.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <div className="rounded-full border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] px-4 py-2 text-sm">
                {formattedDate}
              </div>
              <div className="rounded-full border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] px-4 py-2 text-sm">
                Synced at {formattedTime}
              </div>
              <div className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700">
                Synced with backend
              </div>
            </div>
          <div className="pt-5  w-full">
            <p className="text-xs uppercase tracking-[0.35em] text-[var(--admin-primary)]">Stats</p>
            <div className="mt-4 grid grid-flow-row grid-cols-4 gap-4">
              {metrics.map((metric) => (
                <div key={metric.title} className="rounded-xl w-auto border border-[var(--admin-border)] bg-[var(--admin-surface)] px-3 py-3">
                  <p className="text-xs text-[var(--admin-muted)]">{metric.title}</p>
                  <p className={`text-2xl font-bold ${metric.tone}`}>{metric.value}</p>
                  <p className="text-xs text-[var(--admin-muted)]">{metric.sub}</p>
                </div>
              ))}
            </div>
          </div>
          </div>

        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.5fr_0.85fr]">
        <div className="relative overflow-hidden rounded-[30px] border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-[#102C57]/10 blur-3xl" />
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.32em] text-[var(--admin-primary)]">
                Orders Overview
              </p>
              <h3 className="mt-2 text-2xl font-bold text-[var(--admin-text)]">
                Weekly revenue trend
              </h3>
            </div>
            <div className="rounded-full bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-700">Live</div>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={weeklyRevenueData}>
              <defs>
                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#102C57" stopOpacity={0.32} />
                  <stop offset="95%" stopColor="#102C57" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
              <XAxis dataKey="day" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(value) => `${value / 1000}k`} />
              <Tooltip
                formatter={(value, name) =>
                  name === "revenue" ? formatCurrency(value) : `${value} orders`
                }
                contentStyle={{
                  borderRadius: "18px",
                  border: "1px solid rgba(148,163,184,0.2)",
                  background: "#ffffff",
                  color: "#1f2030",
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#102C57"
                strokeWidth={3}
                fill="url(#revenueFill)"
                animationDuration={1100}
              />
            </AreaChart>
          </ResponsiveContainer>

          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        </div>

        <div className="rounded-[30px] border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
          <p className="text-sm font-semibold uppercase tracking-[0.32em] text-[#1d7a72]">City Performance</p>
          <h3 className="mt-2 text-2xl font-bold text-[var(--admin-text)]">Top cities by orders</h3>

          <div className="mt-5 space-y-3">
            {cityStats.length ? (
              cityStats.map((row) => (
                <div key={row.city} className="flex items-center justify-between rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] px-4 py-3">
                  <div>
                    <p className="font-semibold text-[var(--admin-text)]">{row.city}</p>
                    <p className="text-xs text-[var(--admin-muted)]">Orders</p>
                  </div>
                  <p className="text-xl font-bold text-[var(--admin-primary)]">{row.count}</p>
                </div>
              ))
            ) : (
              <p className="rounded-xl bg-white/60 p-3 text-sm dark:bg-white/5">No city data found.</p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-[30px] border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-[var(--admin-text)]">Recent Orders</h3>
          {loading && <span className="text-xs text-[var(--admin-muted)]">Loading...</span>}
        </div>

        <div className="admin-table-wrap overflow-x-auto">
          <table className="admin-table min-w-full text-sm">
            <thead>
              <tr className="border-b border-[#e6d8c5] text-xs uppercase tracking-[0.18em] text-[#7f5a4f] dark:border-white/10 dark:text-[#e7c98b]">
                
                <th className="px-4 py-3 font-semibold">S.No</th>
                <th className="px-4 py-3 font-semibold">Order ID</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">City</th>
                <th className="px-4 py-3 font-semibold">Pooja/Items</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length ? (
                pagedRecentOrders.map((order, index) => (
                  <tr key={order._id} className="border-b border-[#f0e3d1] align-top last:border-none dark:border-white/10">
                    
                    <td className="px-4 py-3 text-sm text-[#6f3945] dark:text-[#f7e3c0]">{(page - 1) * pageSize + index + 1}</td>
                    <td className="px-4 py-3 font-semibold">#{String(order._id).slice(-6)}</td>
                    <td className="px-4 py-3">{order?.user?.name || "-"}</td>
                    <td className="px-4 py-3">{order?.address?.city || "-"}</td>
                    <td className="px-4 py-3">{order?.itemCount || order?.items?.length || 0}</td>
                    <td className="px-4 py-3">{order?.orderStatus || "Placed"}</td>
                    <td className="px-4 py-3 font-semibold">{formatCurrency(order?.totalAmount || 0)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-4 text-sm text-[var(--admin-muted)]" colSpan={6}>
                    No orders available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={recentOrders.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10]}
        />
      </section>
    </div>
  );
}

